<template>
  <section class="form-fw__content">
    <form
      v-if="status !== isFailed"
      class="form--custom"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <fieldset v-if="hasMoreThanOneCompany" class="form__container">
        <header class="form__container__header">
          <h2 class="form__container__title">{{ $t(`${namespace}.containerCompanyTitle`) }}</h2>
        </header>

        <div class="form__container__content  pb-0">
          <transition name="fade">
            <div v-if="status === isSuccess">
              <label>{{ $t(`${namespace}.fieldCompanyLabel`) }}*</label>
              <div class="form__group">
                <AutoComplete
                  kind="company"
                  :data="company"
                  :placeholder="$t(`${namespace}.fieldCompanyPlaceholder`)"
                  :isValid="isCompanyValid"
                  required
                  @input="updateCompany"
                  @reset="resetFields(true, null, null)"
                />
              </div>
            </div>
          </transition>
        </div>
      </fieldset>

      <fieldset class="form__container" :disabled="status === isLoading">
        <header class="form__container__header">
          <h2 class="form__container__title">{{ $t(`${namespace}.containerCosteCenterTitle`) }}</h2>
          <p class="form__container__desc">{{ $t(`${namespace}.containerCosteCenterDesc`) }}</p>
        </header>

        <div class="form__container__content">
          <label>{{ $t(`${namespace}.fieldCostCenterLabel`) }}*</label>
          <v-text-field
            v-model="form.name"
            :placeholder="$t(`${namespace}.fieldCostCenterPlaceholder`)"
            :error-messages="errors.collect($t(`${namespace}.fieldCostCenterName`))"
            v-validate="{ required: true }"
            :data-vv-name="$t(`${namespace}.fieldCostCenterName`)"
            required
            flat
            outline
            solo
            color="accent"
          />

          <div class="py-2">
            <h2 class="form__container__title">{{ $t(`${namespace}.containerBudgetTitle`) }}</h2>
            <p class="form__container__desc">{{ $t(`${namespace}.containerBudgetDesc`) }}</p>
          </div>

          <v-radio-group v-model="budgetControl" class="mt-2">
            <v-radio
              v-for="item in $t(`${namespace}.fieldBudgetControlItems`)"
              :key="item.value"
              :value="item.value"
            >
              <template slot="label">
                <div v-html="item.label" />
              </template>
            </v-radio>
          </v-radio-group>

          <div v-show="budgetControl === 'manual' || budgetControl === 'automatic'">
            <div v-if="status === isSuccess && isValidObject(company)">
              <label>
                {{ $t(`${namespace}.fieldOwnerLabel`) }}
                <v-tooltip slot="append" top min-width="200" max-width="200">
                  <v-icon color="accent" small slot="activator">info</v-icon>
                  <span>{{ $t(`${namespace}.fieldOwnerTooltip`) }}</span>
                </v-tooltip>
              </label>

              <div class="form__group form__group--auto-height">
                <transition name="fade">
                  <AutoComplete
                    v-if="ownerAutoComplete"
                    kind="owners"
                    :companyId="company ? company.id : null"
                    :data="ownersList"
                    :placeholder="$t(`${namespace}.fieldOwnerPlaceholder`)"
                    multiple
                    :isValid="isOwnerValid"
                    :required="budgetControl !== 'off' || form.ownerEmailOptIn"
                    :validateMessage="ownerValidateMessage"
                    @input="updateOwners"
                    @blur="verifyOwnersList"
                  />
                </transition>
              </div>

              <v-checkbox
                v-model="form.ownerEmailOptIn"
                :label="$t(`${namespace}.fieldOwnerEmailOptInLabel`)"
                class="mt-1 mb-3"
                hide-details
              />
            </div>

            <label>{{ $t(`${namespace}.fieldBudgetLabel`) }}</label>
            <v-text-field
              v-model="budgetMonthlyLimitFormatted"
              v-money="money"
              ref="budgetMonthlyLimitFormatted"
              :placeholder="$t(`${namespace}.fieldBudgetPlaceholder`)"
              :readonly="budgetControl === 'off'"
              :error-messages="errors.collect($t(`${namespace}.fieldBudgetName`))"
              v-validate="{ required: budgetControl !== 'off', isValidValue: budgetControl !== 'off' }"
              :data-vv-name="$t(`${namespace}.fieldBudgetName`)"
              required
              flat
              outline
              solo
              color="accent"
            />

            <label>{{ $t(`${namespace}.fieldExtraBudgetLabel`) }}</label>
            <v-text-field
              v-model="extraMonthlyBudgetFormatted"
              v-money="money"
              ref="extraMonthlyBudgetFormatted"
              :placeholder="$t(`${namespace}.fieldExtraBudgetPlaceholder`)"
              :readonly="budgetControl === 'off'"
              flat
              outline
              solo
              color="accent"
            />
          </div>
        </div>
      </fieldset>
    </form>

    <div
      v-if="status === isFailed"
      class="text-xs-center mt-5"
    >
      <span>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          href="#"
          :title="$t('Common.tryAgain')"
          class="text--link accent--text"
          @click.prevent="$emit('tryAgain')"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </span>
    </div>

    <ChangeOwnerDialog
      :visible="changeOwnerDialog"
      @cancel="prevPage"
      @close="changeOwnerDialog = false"
      @submit="handleSubmit"
    />
  </section>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { VMoney } from 'v-money'
import { isObject, stringToFloat, convertToCoin, goBack } from 'utils/functions'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import AutoComplete from 'components/AutoComplete'
import ChangeOwnerDialog from 'components/costCenter/ChangeOwnerDialog'

const payload = {
  id: null,
  name: null,
  companyId: null,
  temporaryDisabled: false,
  automaticBlock: false,
  ownerEmailOptIn: false,
  owners: [],
  budgetMonthlyLimit: null,
  extraMonthlyBudget: null
}

export default {
  name: 'CostCenterForm',
  props: {
    kind: {
      type: String
    },
    data: {
      type: Object
    },
    submit: {
      type: Boolean
    },
    status: {
      type: Number
    }
  },
  directives: {
    money: VMoney
  },
  components: {
    AutoComplete,
    ChangeOwnerDialog
  },
  computed: {
    ...mapState({
      // globalCompany: (state) => state.company.selectedCompany
      globalCompany: (state) => ({
        id: state.company.selectedCompany.id,
        name: state.company.selectedCompany.name
      }),
      companies: ({ session }) => session.user.business.companies,
      isAdmin: ({ session }) => session.user.business.isAdministrator
    }),

    hasMoreThanOneCompany () {
      return this.isAdmin || this.companies.length > 1
    },

    ownerValidateMessage () {
      if (this.ownersList.length < 1) return this.$t(`${this.namespace}.fieldOwnerErrorMessage`)
      return this.$t(`${this.namespace}.maxOwnersAllowed`)
        .replace('[MIN_LENGTH]', this.minOwnersLength)
        .replace('[MAX_LENGTH]', this.maxOwnersLength)
    },

    isLoading: () => STATUS_IS_LOADING,
    isSuccess: () => STATUS_SUCCESS,
    isFailed: () => STATUS_FAILED
  },
  data () {
    return {
      namespace: 'Views.CostCenterPage',
      isFetching: false,

      // custom validation
      isCompanyValid: true,
      isOwnerValid: true,

      // mask money config
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        precision: 2,
        masked: false
      },

      // auxiliary fields
      company: null,
      ownersList: [],
      ownersListDefault: [],
      budgetMonthlyLimitFormatted: null,
      extraMonthlyBudgetFormatted: null,
      budgetControl: 'off',
      changeOwnerDialog: false,
      ownerAutoComplete: true,
      minOwnersLength: 1,
      maxOwnersLength: 20,

      // form fields
      form: {}
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error'
    }),

    isValidObject: (val) => isObject(val),

    prevPage () {
      goBack()
    },

    verifyOwnersList () {
      const ownersList = this.ownersList.map(item => item.id).sort()
      const ownersListDefault = this.ownersListDefault.map(item => item.id).sort()
      this.changeOwnerDialog = JSON.stringify(ownersList) !== JSON.stringify(ownersListDefault)
    },

    updateOwners (val) {
      if (!this.validateAutoComplete(val)) {
        this.form.ownerEmailOptIn = false
        this.form.owners = []
        this.ownersList = []
      }

      const hasOwnersValid = val.length > 0 && val.length <= this.maxOwnersLength

      if (!hasOwnersValid) {
        this.isOwnerValid = false
        return
      }

      this.ownersList = val.map(owner => ({
        id: owner.id,
        name: owner.name,
        email: owner.email,
        phone: owner.phone
      }))

      this.form.owners = [...this.ownersList]
      this.isOwnerValid = true
    },

    renderPayload () {
      const { id, ...params } = payload
      this.kind === 'editar'
        ? this.form = { id, ...params }
        : this.form = { ...params }
    },

    resetFields (preventReset = false, to = null, from = null) {
      if (
        preventReset ||
        (
          this.isValidObject(to) &&
          this.isValidObject(from) &&
          JSON.stringify(to) !== JSON.stringify(from)
        )
      ) {
        this.isFetching = false

        // custom validation
        this.isCompanyValid = true
        this.isOwnerValid = true

        // auxiliary fields
        this.company = null
        this.ownersList = []
        this.ownersListDefault = []
        this.budgetMonthlyLimitFormatted = null
        this.extraMonthlyBudgetFormatted = null
        this.budgetControl = 'off'

        // form fields
        this.form = {}
        this.renderPayload()
      }
    },

    validateAutoComplete (val) {
      const isArray = Array.isArray(val) && val.length
      const isObject = val && typeof val === 'object' && Object.keys(val).length
      return Boolean(isArray || isObject)
    },

    updateCompany (val) {
      if (!val) {
        this.resetFields(true, null, null)
        return
      }

      if (JSON.stringify(val) !== JSON.stringify(this.company)) {
        if (this.validateAutoComplete(val)) {
          this.company = val
          this.form.companyId = val.id
          this.isCompanyValid = true
        } else {
          this.company = null
          this.form.companyId = null
          this.isCompanyValid = false
        }
      }
    },

    resetMoneyFields () {
      if (this.$refs['budgetMonthlyLimitFormatted']) {
        this.$refs['budgetMonthlyLimitFormatted'].$el.querySelector('input').value = 'R$ 0,00'
        this.form.budgetMonthlyLimit = null
      }

      if (this.$refs['extraMonthlyBudgetFormatted']) {
        this.$refs['extraMonthlyBudgetFormatted'].$el.querySelector('input').value = 'R$ 0,00'
        this.form.extraMonthlyBudget = null
      }
    },

    setCostCenterData (val) {
      if (this.isValidObject(val)) {
        if (!val.enabled) {
          this.$router.push({ name: 'CostCenters' })
          return
        }

        try {
          const { company, ownersList, form } = val

          // auxiliary fields
          this.company = company
          this.ownersList = ownersList
          this.ownersListDefault = ownersList

          this.form = {
            id: form.id,
            name: form.name,
            companyId: form.companyId,
            temporaryDisabled: form.temporaryDisabled,
            automaticBlock: form.automaticBlock,
            ownerEmailOptIn: form.ownerEmailOptIn,
            owners: form.owners,
            budgetMonthlyLimit: form.budgetMonthlyLimit,
            extraMonthlyBudget: form.extraMonthlyBudget
          }

          if (form.budgetMonthlyLimit || form.extraMonthlyBudget) {
            this.budgetControl = form.automaticBlock ? 'automatic' : 'manual'

            if (form.budgetMonthlyLimit) {
              const $budgetMonthlyLimitFormatted = this.$refs['budgetMonthlyLimitFormatted']
              this.budgetMonthlyLimitFormatted = convertToCoin(form.budgetMonthlyLimit)

              if ($budgetMonthlyLimitFormatted) {
                $budgetMonthlyLimitFormatted.$el.querySelector('input').value = convertToCoin(form.budgetMonthlyLimit)
              }
            }

            if (form.extraMonthlyBudget) {
              const $extraMonthlyBudgetFormatted = this.$refs['extraMonthlyBudgetFormatted']
              this.extraMonthlyBudgetFormatted = convertToCoin(form.extraMonthlyBudget)

              if ($extraMonthlyBudgetFormatted) {
                $extraMonthlyBudgetFormatted.$el.querySelector('input').value = convertToCoin(form.extraMonthlyBudget)
              }
            }
          }

          // re-render autocomplete company
          setTimeout(() => { this.isFetching = false }, 200)
        } catch (err) {
          console.log('ERROR', err)
        }
      }
    },

    showErrorMessage (err) {
      let message = null

      if (
        err && err.response && err.response.data && err.response.data.errors &&
        this.isValidObject(err.response.data.errors)
      ) {
        const error = err.response.data.errors
        const key = Object.keys(error)[0]
        const keys = {
          // TO DO
        }

        message = keys[key] || error[key][0]
      } else {
        message = this.$t(`${this.namespace}.costCenterCreateErrorMessage`)
      }

      this.errorAlert(message)
    },

    async createCostCenter () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        await this.$apiService.addCostCenter(this.form)
        this.successAlert(this.$t(`${this.namespace}.costCenterCreateSuccessMessage`))
        this.$emit('setCurrentStatus', this.isSuccess)
        this.resetFields(true, null, null)
        scrollTo(0, 0)
        this.prevPage()
      } catch (err) {
        console.log('ERROR', err)
        this.showErrorMessage(err)
        this.$emit('setCurrentStatus', this.isSuccess)
      }
    },

    async editCostCenter () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        await this.$apiService.updateCostCenter(this.form)
        this.successAlert(this.$t(`${this.namespace}.costCenterEditsuccessMessage`))
        this.$emit('tryAgain')
        scrollTo(0, 0)
        this.prevPage()
      } catch (err) {
        console.log('ERROR', err)
        this.showErrorMessage(err)
        this.$emit('setCurrentStatus', this.isSuccess)
      }
    },

    async handleSubmit () {
      try {
        const isValid = await this.$validator.validateAll()
        const hasOwnersValid = this.ownersList.length > 0 && this.ownersList.length <= this.maxOwnersLength

        if (!this.company) this.isCompanyValid = false

        if ((this.budgetControl !== 'off' || this.form.ownerEmailOptIn) && !hasOwnersValid) {
          this.isOwnerValid = false
        }

        if (isValid && this.isCompanyValid && this.isOwnerValid) {
          this.kind === 'editar'
            ? this.editCostCenter()
            : this.createCostCenter()
        } else {
          const target = this.$validator.errors.items[0]
          if (target) {
            const elem = this.$el.querySelector(`[data-vv-name="${target.field}"]`)
            if (elem) {
              elem.scrollIntoView(false)
              elem.focus()
            }
          }
        }
      } catch (err) {
        console.log('ERROR', err)
      }
    }
  },
  watch: {
    kind () {
      this.renderPayload()
    },

    submit () {
      this.handleSubmit()
    },

    company (to, from) {
      this.resetFields(false, to, from)
    },

    data (val) {
      this.setCostCenterData(val)
    },

    budgetMonthlyLimitFormatted (val) {
      this.form.budgetMonthlyLimit = stringToFloat(val)
    },

    extraMonthlyBudgetFormatted (val) {
      this.form.extraMonthlyBudget = stringToFloat(val)
    },

    budgetControl (val) {
      if (val === 'manual') {
        window.setTimeout(() => {
          this.form.automaticBlock = false
          this.ownerAutoComplete = true
        }, 100)
        return
      }

      if (val === 'automatic') {
        window.setTimeout(() => {
          this.form.automaticBlock = true
          this.ownerAutoComplete = true
        }, 100)
        return
      }

      if (val === 'off') {
        this.form.owners = []
        this.ownersList = []
        this.isOwnerValid = true
        this.form.ownerEmailOptIn = false
        this.ownerAutoComplete = false
        this.resetMoneyFields()
      }
    }
  },
  created () {
    this.$validator.extend('isValidValue', {
      getMessage: field => this.$t('Common.pleaseEnterValidValue'),
      validate: value => !/(R\$\s0,00)$/.test(value)
    })
    this.renderPayload()
  },
  mounted () {
    this.isFetching = true

    if (this.kind !== 'editar' && this.globalCompany) {
      this.updateCompany(this.globalCompany)
      setTimeout(() => { this.isFetching = false }, 200)
      return
    }

    this.setCostCenterData(this.data)
  }
}
</script>

<style lang="scss" scoped>
.form__group {
  height: 85.95px;
}
</style>
